import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import { GridColDef } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { FilterableDataTable, DrawerPanel, ConfirmDialog } from '../../components/shared';
import type { RowAction, DrawerAction } from '../../components/shared';
import {
  MachineChecklistCategoryControllerApi,
  MachineControllerApi,
  MachinePartControllerApi,
} from '../../api-generated/apis';
import type { MachineChecklistCategory, Machine, MachinePart } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';

interface CategoryRow {
  id: string | number;
  name: string;
  machineId: number | null;
  machineName: string;
  machinePartId: number | null;
  machinePartName: string;
  active: boolean;
}

interface MachineOption {
  id: number;
  name: string;
}

interface PartOption {
  id: number;
  name: string;
  machineId: number | null;
}

const categoryApi = new MachineChecklistCategoryControllerApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const machinePartApi = new MachinePartControllerApi(apiConfig);

const columns: GridColDef[] = [
  { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
  { field: 'machineName', headerName: 'Machine', flex: 0.8, minWidth: 130 },
  { field: 'machinePartName', headerName: 'Part', flex: 0.8, minWidth: 130 },
  {
    field: 'active',
    headerName: 'Active',
    flex: 0.4,
    minWidth: 80,
    renderCell: (params) => (
      <Typography variant="body2">{params.value ? 'Yes' : 'No'}</Typography>
    ),
  },
];

const CategoriesPanel: React.FC = () => {
  const queryClient = useQueryClient();

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formMachineId, setFormMachineId] = useState<number | null>(null);
  const [formPartId, setFormPartId] = useState<number | null>(null);
  const [formActive, setFormActive] = useState(true);
  const [errors, setErrors] = useState<{ name?: string }>({});

  // Confirm dialog state
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  // Queries
  const { data: categories, isLoading } = useQuery<MachineChecklistCategory[]>({
    queryKey: ['checklist-categories'],
    queryFn: () => categoryApi.getAllCategories(),
  });

  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines', 'options'],
    queryFn: () => machineApi.getAllMachines(),
  });

  const partsQuery = useQuery<MachinePart[]>({
    queryKey: ['machine-parts'],
    queryFn: () => machinePartApi.getAllMachineParts(),
  });

  // Derived data
  const machineOptions: MachineOption[] = useMemo(
    () => (machinesQuery.data ?? []).map((m: any) => ({ id: Number(m.id) || 0, name: m.name ?? '' })),
    [machinesQuery.data],
  );

  const allParts: PartOption[] = useMemo(
    () => (partsQuery.data ?? []).map((p: any) => ({
      id: Number(p.id) || 0,
      name: p.name ?? '',
      machineId: null,
    })),
    [partsQuery.data],
  );

  const filteredParts: PartOption[] = useMemo(() => {
    if (!formMachineId) return allParts;
    return allParts.filter((p) => p.machineId === formMachineId);
  }, [allParts, formMachineId]);

  const rows: CategoryRow[] = useMemo(
    () => (categories ?? []).map((c: any) => ({
      id: Number(c.id) || 0,
      name: c.name ?? '',
      machineId: null,
      machineName: '',
      machinePartId: null,
      machinePartName: '',
      active: c.active ?? true,
    })),
    [categories],
  );

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (data: { id?: number; name: string; machineId: number | null; machinePartId: number | null; active: boolean }) => {
      const payload: MachineChecklistCategory = { name: data.name, active: data.active };
      if (data.id) {
        return categoryApi.updateCategory({ id: String(data.id), machineChecklistCategory: payload } as any);
      }
      return categoryApi.createCategory({ machineChecklistCategory: payload } as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-categories'] });
      setDrawerOpen(false);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => categoryApi.deleteCategory({ id: String(id) } as any),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist-categories'] });
      setConfirmOpen(false);
    },
  });

  // Handlers
  const handleAdd = useCallback(() => {
    setEditingId(null);
    setFormName('');
    setFormMachineId(null);
    setFormPartId(null);
    setFormActive(true);
    setErrors({});
    setDrawerOpen(true);
  }, []);

  const handleEdit = useCallback((row: CategoryRow) => {
    setEditingId(Number(row.id));
    setFormName(row.name);
    setFormMachineId(row.machineId);
    setFormPartId(row.machinePartId);
    setFormActive(row.active);
    setErrors({});
    setDrawerOpen(true);
  }, []);

  const handleSave = useCallback(() => {
    if (!formName.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }
    saveMutation.mutate({
      id: editingId ?? undefined,
      name: formName.trim(),
      machineId: formMachineId,
      machinePartId: formPartId,
      active: formActive,
    });
  }, [formName, formMachineId, formPartId, formActive, editingId, saveMutation]);

  // Row actions
  const rowActions: RowAction<CategoryRow>[] = [
    { label: 'Edit', icon: EditIcon, onClick: handleEdit },
    { label: 'Delete', icon: DeleteIcon, onClick: (row) => { setDeleteId(Number(row.id)); setConfirmOpen(true); } },
  ];

  // Drawer actions
  const drawerActions: DrawerAction[] = [
    { label: 'Cancel', variant: 'outlined', onClick: () => setDrawerOpen(false) },
    { label: editingId ? 'Update' : 'Create', variant: 'contained', onClick: handleSave, disabled: !formName },
  ];

  return (
    <>
      <FilterableDataTable<CategoryRow>
        rows={rows}
        columns={columns}
        loading={isLoading}
        totalCount={rows.length}
        page={0}
        pageSize={25}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        actions={rowActions}
        primaryAction={{ label: 'Add Category', onClick: handleAdd }}
        searchPlaceholder="Search categories..."
      />
      <DrawerPanel
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        title={editingId ? 'Edit Category' : 'New Category'}
        width="narrow"
        actions={drawerActions}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name"
            value={formName}
            onChange={(e) => { setFormName(e.target.value); setErrors({}); }}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="cat-machine-label">Machine</InputLabel>
            <Select
              labelId="cat-machine-label"
              value={formMachineId ?? ''}
              label="Machine"
              onChange={(e: SelectChangeEvent<number | string>) => {
                const val = e.target.value;
                setFormMachineId(val === '' ? null : Number(val));
                setFormPartId(null);
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {machineOptions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="cat-part-label">Part</InputLabel>
            <Select
              labelId="cat-part-label"
              value={formPartId ?? ''}
              label="Part"
              onChange={(e: SelectChangeEvent<number | string>) => {
                const val = e.target.value;
                setFormPartId(val === '' ? null : Number(val));
              }}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {filteredParts.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={formActive}
                onChange={(e) => setFormActive(e.target.checked)}
              />
            }
            label="Active"
          />
        </Box>
      </DrawerPanel>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => deleteId != null && deleteMutation.mutate(deleteId)}
        title="Delete Category"
        message="Are you sure you want to delete this category? This action cannot be undone."
        confirmLabel="Delete"
        confirmColor="error"
      />
    </>
  );
};

export default CategoriesPanel;
